import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'

// Revenue Dashboard Loading Skeleton
export const RevenueDashboardSkeleton: React.FC = () => (
  <Box>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Skeleton variant="text" width={300} height={40} />
      <Skeleton variant="rectangular" width={200} height={36} />
    </Box>

    {/* Key Metrics Skeleton */}
    <Grid container spacing={3} mb={3}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} md={3} key={i}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="80%" height={40} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
                <Skeleton variant="circular" width={40} height={40} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    {/* Chart Skeleton */}
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={300} />
      </CardContent>
    </Card>

    {/* Bar Chart Skeleton */}
    <Card>
      <CardContent>
        <Skeleton variant="text" width={250} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={300} />
      </CardContent>
    </Card>
  </Box>
)

// Financial Reports Loading Skeleton
export const FinancialReportsSkeleton: React.FC = () => (
  <Box>
    <Skeleton variant="text" width={250} height={40} sx={{ mb: 3 }} />

    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
        <Box display="flex" gap={2} mb={2}>
          <Skeleton variant="rectangular" width={150} height={40} />
          <Skeleton variant="rectangular" width={150} height={40} />
          <Skeleton variant="rectangular" width={120} height={36} />
        </Box>
      </CardContent>
    </Card>

    <Card>
      <CardContent>
        <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <TableCell key={i}>
                    <Skeleton variant="text" width="100%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3].map((row) => (
                <TableRow key={row}>
                  {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                    <TableCell key={cell}>
                      <Skeleton variant="text" width="100%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  </Box>
)

// GUI Agent Status Loading Skeleton
export const GUIAgentStatusSkeleton: React.FC = () => (
  <Box>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Skeleton variant="text" width={200} height={40} />
      <Skeleton variant="rectangular" width={100} height={36} />
    </Box>

    {/* System Health Cards */}
    <Grid container spacing={2} mb={3}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} md={3} key={i}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Skeleton variant="text" width={100} height={16} />
                  <Skeleton variant="text" width={80} height={24} />
                </Box>
                <Skeleton variant="circular" width={24} height={24} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    {/* Agent Status Table */}
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <TableCell key={i}>
                    <Skeleton variant="text" width="100%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2].map((row) => (
                <TableRow key={row}>
                  {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                    <TableCell key={cell}>
                      {cell === 7 ? (
                        <Box display="flex" gap={1}>
                          <Skeleton variant="circular" width={24} height={24} />
                          <Skeleton variant="circular" width={24} height={24} />
                        </Box>
                      ) : (
                        <Skeleton variant="text" width="100%" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>

    {/* Performance Metrics */}
    <Card>
      <CardContent>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="text" width="40%" height={16} sx={{ mb: 1 }} />
              <Box display="flex" alignItems="center" gap={1}>
                <Box flexGrow={1}>
                  <Skeleton variant="rectangular" width="100%" height={8} />
                </Box>
                <Skeleton variant="text" width={40} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  </Box>
)

// Store Comparison Loading Skeleton
export const StoreComparisonSkeleton: React.FC = () => (
  <Box>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Skeleton variant="text" width={200} height={40} />
      <Skeleton variant="rectangular" width={200} height={36} />
    </Box>

    <Grid container spacing={3} mb={3}>
      {[1, 2, 3].map((i) => (
        <Grid item xs={12} md={4} key={i}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={200} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={3}>
      {[1, 2].map((i) => (
        <Grid item xs={12} md={6} key={i}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={300} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
)

// Generic Card Skeleton
export const CardSkeleton: React.FC<{ height?: number }> = ({ height = 200 }) => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={height} />
    </CardContent>
  </Card>
)

// Metric Card Skeleton
export const MetricCardSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="text" width="80%" height={40} />
      <Skeleton variant="text" width="40%" height={16} />
    </CardContent>
  </Card>
)